import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import apiClient from '../client';
import {
  MeetingAnnouncementView,
  MeetingAnnouncementInput,
  MeetingAnnouncementStatusUpdateInput,
  MeetingAnnouncementSearchParams,
  ApiError,
  ApiPlatformCollectionResponse,
  extractCollectionItems,
} from '../types';

export const meetingAnnouncementKeys = {
  all: ['meetingAnnouncements'] as const,
  lists: () => [...meetingAnnouncementKeys.all, 'list'] as const,
  list: (params?: MeetingAnnouncementSearchParams) =>
    [...meetingAnnouncementKeys.lists(), params] as const,
  details: () => [...meetingAnnouncementKeys.all, 'detail'] as const,
  detail: (id: string) => [...meetingAnnouncementKeys.details(), id] as const,
};

export const useMeetingAnnouncements = (
  params?: MeetingAnnouncementSearchParams,
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementView[], ApiError>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<MeetingAnnouncementView[], ApiError>({
    queryKey: meetingAnnouncementKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiPlatformCollectionResponse<MeetingAnnouncementView>>('/meeting-announcements', params);
      return extractCollectionItems(response.data);
    },
    ...options,
  });
};

export const useMyMeetingAnnouncements = (
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementView[], ApiError>,
    'queryKey' | 'queryFn'
  >
) => {
  return useMeetingAnnouncements(
    { owned_only: true },
    options
  );
};

export const useMeetingAnnouncement = (
  id: string,
  options?: Omit<
    UseQueryOptions<MeetingAnnouncementView, ApiError>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<MeetingAnnouncementView, ApiError>({
    queryKey: meetingAnnouncementKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<MeetingAnnouncementView>(
        `/meeting-announcements/${id}`
      );
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCreateMeetingAnnouncement = (
  options?: UseMutationOptions<
    MeetingAnnouncementView,
    ApiError,
    MeetingAnnouncementInput
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementView,
    ApiError,
    MeetingAnnouncementInput
  >({
    mutationFn: async (data: MeetingAnnouncementInput) => {
      const response = await apiClient.post<MeetingAnnouncementView>(
        '/meeting-announcements',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: meetingAnnouncementKeys.lists(),
      });
    },
    ...options,
  });
};

export const useUpdateMeetingAnnouncement = (
  options?: UseMutationOptions<
    MeetingAnnouncementView,
    ApiError,
    { id: string; data: MeetingAnnouncementInput }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementView,
    ApiError,
    { id: string; data: MeetingAnnouncementInput }
  >({
    mutationFn: async ({ id, data }: { id: string; data: MeetingAnnouncementInput }) => {
      const response = await apiClient.put<MeetingAnnouncementView>(
        `/meeting-announcements/${id}`,
        data
      );
      return response.data;
    },
    onSuccess: (data: MeetingAnnouncementView, variables: { id: string; data: MeetingAnnouncementInput }) => {
      queryClient.setQueryData(
        meetingAnnouncementKeys.detail(variables.id),
        data
      );
      queryClient.invalidateQueries({
        queryKey: meetingAnnouncementKeys.lists(),
      });
    },
    ...options,
  });
};

export const useUpdateMeetingAnnouncementStatus = (
  options?: UseMutationOptions<
    MeetingAnnouncementView,
    ApiError,
    { id: string; data: MeetingAnnouncementStatusUpdateInput }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingAnnouncementView,
    ApiError,
    { id: string; data: MeetingAnnouncementStatusUpdateInput }
  >({
    mutationFn: async ({ id, data }: { id: string; data: MeetingAnnouncementStatusUpdateInput }) => {
      const response = await apiClient.post<MeetingAnnouncementView>(
        `/meeting-announcements/${id}/status`,
        data
      );
      return response.data;
    },
    onSuccess: (data: MeetingAnnouncementView, variables: { id: string; data: MeetingAnnouncementStatusUpdateInput }) => {
      queryClient.setQueryData(
        meetingAnnouncementKeys.detail(variables.id),
        data
      );
      queryClient.invalidateQueries({
        queryKey: meetingAnnouncementKeys.lists(),
      });
    },
    ...options,
  });
};
